import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    BroadcastMessageVersion,
    BroadcastMessageWithStatistic,
    IPagination,
    ISorting,
    LoadStatus,
} from '../../../model';
import { RootState } from '../..';
import {
    loadBroadcastMessages,
    loadBroadcastMessageVersions,
} from './broadcastMessageReportSlice.thunks';

export interface BroadcastMessageReportFilterState {
    queryText: string;
}

export interface BroadcastMessageReportState {
    items: BroadcastMessageWithStatistic[];
    pagination: IPagination;
    sorting: ISorting;

    loadStatus: LoadStatus;
    filter: BroadcastMessageReportFilterState;

    versions: {
        loadStatus: LoadStatus;
        items: BroadcastMessageVersion[];
    };
}

const initialState: BroadcastMessageReportState = {
    loadStatus: 'loading',
    items: [],
    pagination: {
        currentPage: 1,
        pageSize: 10,
        totalCount: 0,
    },
    filter: {
        queryText: '',
    },
    sorting: {
        sortBy: 'createdOnUtc',
        sortByDescending: true,
    },
    versions: {
        loadStatus: 'idle',
        items: [],
    },
};

export const broadcastMessageReportSlice = createSlice({
    name: 'broadcastMessageReport',
    initialState,
    reducers: {
        setCurrentPage: (state, action: PayloadAction<number>) => {
            state.pagination.currentPage =
                action.payload < 1 ? 1 : action.payload;
        },
        setSorting: (state, action: PayloadAction<string>) => {
            if (state.sorting.sortBy === action.payload) {
                state.sorting.sortByDescending =
                    !state.sorting.sortByDescending;
            } else {
                state.sorting.sortBy = action.payload;
                state.sorting.sortByDescending = false;
            }
        },
        setLoadStatus: (state, action) => {
            state.loadStatus = action.payload;
        },
        setFilter: (
            state,
            action: PayloadAction<BroadcastMessageReportFilterState>
        ) => {
            state.filter = { ...state.filter, ...action.payload };
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadBroadcastMessages.pending, (state) => {
                state.loadStatus = 'loading';
            })
            .addCase(loadBroadcastMessages.fulfilled, (state, action) => {
                state.loadStatus = 'idle';
                state.items = action.payload.value;

                if (action.payload['@odata.count'] !== undefined) {
                    state.pagination.totalCount =
                        action.payload['@odata.count'] ?? 0;
                }
            })
            .addCase(loadBroadcastMessages.rejected, (state) => {
                state.loadStatus = 'failed';
            })
            .addCase(loadBroadcastMessageVersions.pending, (state) => {
                state.versions.loadStatus = 'loading';
            })
            .addCase(
                loadBroadcastMessageVersions.fulfilled,
                (state, action) => {
                    state.versions.loadStatus = 'idle';
                    state.versions.items = action.payload.value;
                }
            )
            .addCase(loadBroadcastMessageVersions.rejected, (state) => {
                state.loadStatus = 'failed';
            });
    },
});

export const { setCurrentPage, setSorting, setLoadStatus, setFilter } =
    broadcastMessageReportSlice.actions;

export const broadcastMessageReport = (state: RootState) =>
    state.broadcastMessageReport;
export const broadcastMessageReportFilter = (state: RootState) =>
    state.broadcastMessageReport.filter;

export default broadcastMessageReportSlice.reducer;
